import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  CircularProgress,
  Paper,
  Snackbar,
  Alert,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Send,
  MoreVert,
  ArrowBack,
  AttachFile,
  InsertEmoticon,
  Visibility,
  DoneAll,
  Done,
  Schedule,
  ErrorOutline,
  Search,
  Call,
  Videocam,
  Info,
  Image as ImageIcon,
  Mic,
} from '@mui/icons-material';
import ReadReceipts from './ReadReceipts';
import CustomTextArea from '../../CustomForm/CustomTextArea';

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateDivider = (ts) => {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  const diff = now - date;
  const dayDiff = Math.floor(diff / 86400000);

  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const StatusIcon = ({ status, readCount, isGroup }) => {
  if (status === 'SENDING') {
    return <Schedule sx={{ fontSize: 12, color: '#94A3B8' }} />;
  }
  if (status === 'FAILED') {
    return <ErrorOutline sx={{ fontSize: 12, color: '#EF4444' }} />;
  }
  if (readCount > 0) {
    return (
      <DoneAll
        sx={{ fontSize: 14, color: isGroup ? '#1570EF' : '#34B7F1' }}
      />
    );
  }
  return <Done sx={{ fontSize: 14, color: '#94A3B8' }} />;
};

const MessageChat = ({
  conversation,
  messages,
  userId,
  onSend,
  newMessage,
  setNewMessage,
  isSending,
  isLoading,
  onBack,
  onMarkAsRead,
  isConnected,
}) => {
  const messagesContainerRef = useRef(null);
  const processedIds = useRef(new Set());
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [readReceiptsOpen, setReadReceiptsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isSendingRef = useRef(false);

  const messagesWithDividers = useMemo(() => {
    const result = [];
    let lastDate = null;
    messages.forEach((msg, index) => {
      const msgDate = msg.timestamp;
      if (!lastDate || !isSameDay(lastDate, msgDate)) {
        result.push({
          type: 'divider',
          id: `div-${msg.id || index}`,
          date: msgDate,
        });
        lastDate = msgDate;
      }
      result.push({ type: 'message', id: msg.id || index, msg, index });
    });
    return result;
  }, [messages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;

    const raf = requestAnimationFrame(() => {
      if (container) container.scrollTop = container.scrollHeight;
    });
    const timer = setTimeout(() => {
      if (container) container.scrollTop = container.scrollHeight;
    }, 100);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [messages, conversation?.id]);

  useEffect(() => {
    if (!messages.length || !conversation) return;
    const unread = messages.filter(
      (msg) =>
        msg.sender?.id !== userId &&
        !msg.read_by?.some((r) => r.id === userId) &&
        !processedIds.current.has(msg.id)
    );
    if (unread.length > 0) {
      unread.forEach((msg) => {
        processedIds.current.add(msg.id);
        onMarkAsRead?.(msg.id);
      });
    }
  }, [messages, conversation, userId, onMarkAsRead]);

  useEffect(() => {
    processedIds.current.clear();
  }, [conversation?.id]);

  const handleReadReceiptClick = (msg) => {
    if (conversation?.is_group && msg.read_by?.length > 0) {
      setSelectedMessage(msg);
      setReadReceiptsOpen(true);
    }
  };

  const getName = () => {
    if (!conversation) return 'Select a conversation';
    if (conversation.is_group && conversation.group)
      return conversation.group.name || 'Group Chat';
    const others = conversation.participants?.filter((p) => p.id !== userId) || [];
    if (others.length === 1)
      return (
        `${others[0].first_name} ${others[0].last_name}`.trim() || 'Unknown'
      );
    if (others.length > 1) return others.map((p) => p.first_name).join(', ');
    return 'New Conversation';
  };

  const getSubtitle = () => {
    if (!conversation) return '';
    if (conversation.is_group) {
      const count = conversation.participants?.length || 0;
      return `${count} participants`;
    }
    return isConnected ? 'Online' : 'Offline';
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) {
      setError('Please enter a message');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!isConnected) {
      setError('Not connected to server');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (isSendingRef.current) return;

    isSendingRef.current = true;
    onSend();

    setTimeout(() => {
      isSendingRef.current = false;
    }, 1000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const renderDivider = (divider) => (
    <Box
      key={divider.id}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        my: 2,
      }}
    >
      <Chip
        label={formatDateDivider(divider.date)}
        size="small"
        sx={{
          bgcolor: '#FFFFFF',
          color: '#64748B',
          fontSize: 11,
          fontWeight: 600,
          height: 24,
          px: 1,
          border: '1px solid #E2E8F0',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      />
    </Box>
  );

  const renderMessage = (msg, index) => {
    const isMe = msg.sender?.id === userId;
    const prevMsg = messages[index - 1];
    const nextMsg = messages[index + 1];
    const showAvatar =
      !isMe && (!prevMsg || prevMsg.sender?.id !== msg.sender?.id);
    const isLastInGroup =
      !nextMsg ||
      nextMsg.sender?.id !== msg.sender?.id ||
      !isSameDay(nextMsg.timestamp, msg.timestamp);

    const readCount = msg.read_by?.length || 0;
    const isGroup = conversation?.is_group || false;
    const showReadReceipt = isGroup && readCount > 0 && isMe;
    const isSendingStatus = msg.status === 'SENDING';
    const isFailed = msg.status === 'FAILED';

    const radius = isMe
      ? isLastInGroup
        ? '16px 16px 4px 16px'
        : '16px 16px 16px 16px'
      : isLastInGroup
      ? '16px 16px 16px 4px'
      : '16px 16px 16px 16px';

    return (
      <Box
        key={msg.id || index}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMe ? 'flex-end' : 'flex-start',
          mb: isLastInGroup ? 1.5 : 0.4,
          px: { xs: 1.5, md: 2 },
          opacity: isSendingStatus ? 0.7 : 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            maxWidth: { xs: '85%', md: '70%' },
          }}
        >
          {!isMe && (
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: 11,
                bgcolor: '#7C3AED',
                color: '#FFFFFF',
                fontWeight: 600,
                flexShrink: 0,
                opacity: showAvatar && isLastInGroup ? 1 : 0,
                transition: 'opacity 0.15s ease',
              }}
            >
              {getInitials(
                msg.sender?.first_name || msg.sender?.username || ''
              )}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            {!isMe && showAvatar && (
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: '#7C3AED',
                  mb: 0.25,
                  ml: 0.5,
                }}
              >
                {msg.sender?.first_name || msg.sender?.username || 'Unknown'}
              </Typography>
            )}
            <Tooltip
              title={
                showReadReceipt ? 'Click to view read receipts' : ''
              }
              arrow
              placement={isMe ? 'left' : 'right'}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 1.75,
                  py: 1,
                  bgcolor: isMe
                    ? isFailed
                      ? '#FEE2E2'
                      : '#1570EF'
                    : '#FFFFFF',
                  color: isMe
                    ? isFailed
                      ? '#991B1B'
                      : '#FFFFFF'
                    : '#0F172A',
                  borderRadius: radius,
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                  boxShadow: isMe
                    ? '0 1px 2px rgba(21, 112, 239, 0.2)'
                    : '0 1px 2px rgba(0,0,0,0.05)',
                  border: isMe ? 'none' : '1px solid #E9EDEF',
                  cursor: showReadReceipt ? 'pointer' : 'default',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': showReadReceipt
                    ? {
                        transform: 'translateY(-1px)',
                        boxShadow:
                          '0 4px 12px rgba(21, 112, 239, 0.25)',
                      }
                    : {},
                }}
                onClick={() =>
                  showReadReceipt && handleReadReceiptClick(msg)
                }
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: isMe
                      ? isFailed
                        ? '#991B1B'
                        : '#FFFFFF'
                      : '#0F172A',
                  }}
                >
                  {msg.content}
                </Typography>
              </Paper>
            </Tooltip>
            {isLastInGroup && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: isMe ? 'flex-end' : 'flex-start',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.35,
                  px: 0.5,
                }}
              >
                <Typography
                  sx={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 500 }}
                >
                  {formatTime(msg.timestamp)}
                </Typography>
                {isMe && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.3,
                      cursor: showReadReceipt ? 'pointer' : 'default',
                    }}
                    onClick={() =>
                      showReadReceipt && handleReadReceiptClick(msg)
                    }
                  >
                    <StatusIcon
                      status={msg.status}
                      readCount={readCount}
                      isGroup={isGroup}
                    />
                    {isGroup && readCount > 0 && (
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          color: '#1570EF',
                          fontWeight: 600,
                        }}
                      >
                        {readCount}
                      </Typography>
                    )}
                    {isFailed && (
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          color: '#EF4444',
                          fontWeight: 600,
                        }}
                      >
                        Failed
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  if (!conversation) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F8FAFC',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            bgcolor: '#EFF6FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
          }}
        >
          <Info sx={{ color: '#1570EF', fontSize: 40 }} />
        </Box>
        <Typography
          sx={{
            color: '#0F172A',
            fontWeight: 700,
            fontSize: 18,
            mb: 0.75,
          }}
        >
          Welcome to Messages
        </Typography>
        <Typography
          sx={{
            color: '#94A3B8',
            fontSize: 13.5,
            textAlign: 'center',
            maxWidth: 300,
            lineHeight: 1.5,
          }}
        >
          Select a conversation from the sidebar to start chatting, or search
          for someone new
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#F8FAFC',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid #E2E8F0',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
            flex: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={onBack}
            sx={{ display: { xs: 'flex', md: 'none' }, color: '#0F172A' }}
          >
            <ArrowBack sx={{ fontSize: 20 }} />
          </IconButton>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: conversation.is_group ? '#7C3AED' : '#1570EF',
              fontSize: 15,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {getInitials(getName())}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: 15.5,
                fontWeight: 700,
                color: '#0F172A',
                lineHeight: 1.2,
              }}
              noWrap
            >
              {getName()}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 0.25,
              }}
            >
              {conversation.is_group ? (
                <Typography
                  sx={{ fontSize: 11.5, color: '#64748B', fontWeight: 500 }}
                >
                  {getSubtitle()}
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isConnected ? '#10B981' : '#94A3B8',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: isConnected ? '#10B981' : '#94A3B8',
                      fontWeight: 600,
                    }}
                  >
                    {isConnected ? 'Online' : 'Offline'}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Search in chat" arrow>
            <IconButton size="small" sx={{ color: '#64748B' }}>
              <Search sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Voice call" arrow>
            <IconButton size="small" sx={{ color: '#64748B' }}>
              <Call sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Video call" arrow>
            <IconButton size="small" sx={{ color: '#64748B' }}>
              <Videocam sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: '#64748B' }}
          >
            <MoreVert sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            mt: 0.5,
            minWidth: 180,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <MenuItem onClick={handleMenuClose} sx={{ fontSize: 13.5, py: 1 }}>
          View contact info
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ fontSize: 13.5, py: 1 }}>
          Search messages
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ fontSize: 13.5, py: 1 }}>
          Media & files
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleMenuClose}
          sx={{ fontSize: 13.5, py: 1, color: '#EF4444' }}
        >
          Clear chat
        </MenuItem>
      </Menu>

      {/* Messages */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#F8FAFC',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#CBD5E1',
            borderRadius: 3,
            '&:hover': { bgcolor: '#94A3B8' },
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress size={28} sx={{ color: '#1570EF' }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 3,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Send sx={{ color: '#1570EF', fontSize: 32 }} />
            </Box>
            <Typography
              sx={{
                color: '#0F172A',
                fontSize: 15,
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              No messages yet
            </Typography>
            <Typography
              sx={{
                color: '#94A3B8',
                fontSize: 13,
                textAlign: 'center',
                maxWidth: 260,
                lineHeight: 1.5,
              }}
            >
              Send your first message to start the conversation
            </Typography>
          </Box>
        ) : (
          messagesWithDividers.map((item) =>
            item.type === 'divider'
              ? renderDivider(item)
              : renderMessage(item.msg, item.index)
          )
        )}
      </Box>

      {/* Input */}
      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: 1.5,
          bgcolor: '#FFFFFF',
          borderTop: '1px solid #E2E8F0',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            bgcolor: '#F8FAFC',
            borderRadius: 3,
            px: 1,
            py: 0.75,
            border: '1px solid #E2E8F0',
            transition: 'border-color 0.15s ease',
            '&:focus-within': {
              borderColor: '#1570EF',
              bgcolor: '#FFFFFF',
              boxShadow: '0 0 0 3px rgba(21, 112, 239, 0.1)',
            },
          }}
        >
          <Tooltip title="Emoji" arrow>
            <IconButton size="small" sx={{ color: '#64748B' }}>
              <InsertEmoticon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Attach file" arrow>
            <IconButton size="small" sx={{ color: '#64748B' }}>
              <AttachFile sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Send image" arrow>
            <IconButton
              size="small"
              sx={{ color: '#64748B', display: { xs: 'none', md: 'flex' } }}
            >
              <ImageIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CustomTextArea
              rows={3}
              placeholder={
                isConnected ? 'Type a message...' : 'Connecting...'
              }
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!isConnected}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'transparent',
                  fontSize: 14,
                  p: 0.5,
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                },
              }}
            />
          </Box>

          <Tooltip title="Voice message" arrow>
            <IconButton
              size="small"
              sx={{ color: '#64748B', display: { xs: 'none', md: 'flex' } }}
            >
              <Mic sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isSending || !isConnected}
            sx={{
              bgcolor: '#1570EF',
              color: '#FFFFFF',
              width: 38,
              height: 38,
              flexShrink: 0,
              '&:hover': { bgcolor: '#0E5FD9' },
              '&.Mui-disabled': {
                bgcolor: '#E2E8F0',
                color: '#94A3B8',
              },
              transition: 'all 0.15s ease',
            }}
          >
            {isSending ? (
              <CircularProgress size={18} sx={{ color: '#FFFFFF' }} />
            ) : (
              <Send sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      <ReadReceipts
        message={selectedMessage}
        open={readReceiptsOpen}
        onClose={() => {
          setReadReceiptsOpen(false);
          setSelectedMessage(null);
        }}
        participants={conversation.participants}
      />
    </Box>
  );
};

export default MessageChat;